/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, computed, signal} from '@angular/core';
import template from './pagination.component.html';
import type {SignalPaginationState} from '../../controller/signal-page-state';
import {visiblePageNumbers} from './visible-page-numbers';

@Component({
  standalone: false,
  selector: 'adn-pagination',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdnPaginationComponent {
  private readonly listInput = signal<any>(null);
  @Input() set list(value: any) { this.listInput.set(value); }
  get list(): any { return this.listInput(); }

  private readonly directState = signal<SignalPaginationState>(null);
  @Input() set state(value: SignalPaginationState) { this.directState.set(value); }
  private readonly source = computed<SignalPaginationState>(() => this.directState() || this.listInput()?.listState);

  readonly pageSize = computed(() => this.source()?.pageSize || 25);
  readonly totalCount = computed(() => this.source()?.totalCount() || 0);
  readonly currentPage = computed(() => this.source()?.currentPage() || 1);
  readonly pageQueryResolved = computed(() => !this.source()?.loading());
  readonly hasItems = computed(() => !!this.source()?.items().length);

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalCount() / Math.max(1, this.pageSize())))
  );
  readonly pageRangeStart = computed(() =>
    this.pageSize() * (this.currentPage() - 1) + 1
  );
  readonly pageRangeEnd = computed(() =>
    Math.min(this.pageSize() * this.currentPage(), this.totalCount())
  );
  readonly pageNumbers = computed(() =>
    visiblePageNumbers(this.currentPage(), this.totalPages())
  );

  changePage(page: number): void {
    this.source()?.changePage(page, {preventDefault: () => undefined} as Event);
  }
}
